import React, { useEffect, useState, lazy, Suspense } from "react";
import WebApp from "@twa-dev/sdk";
import { Routes, Route, Navigate } from "react-router-dom";

// dev-only preview: open with ?preview=1 to skip auth and see the new UI
const PREVIEW =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get("preview") === "1";

if (!PREVIEW) {
  try {
    WebApp.ready();
    // Disable Telegram's swipe-to-close gesture. Without this, dragging
    // any Discover card down (or even sometimes sideways near the edge)
    // dismisses the entire mini app. Added in Bot API 7.7; older clients
    // simply ignore the call.
    if (typeof WebApp.disableVerticalSwipes === "function") {
      WebApp.disableVerticalSwipes();
    }
    // Force the mini app to full-height so closing-via-collapse is gone.
    if (typeof WebApp.expand === "function") {
      WebApp.expand();
    }
  } catch (e) {
    console.warn("WebApp.ready() failed (non-Telegram env):", e);
  }
}

import { Toaster } from "react-hot-toast";
import { TOAST_STYLE } from "./lib/toast";

// Telegram login is the first-paint critical path (almost all users land here)
// → import eagerly. The plain email/password LoginPage is a rare fallback,
// so it gets lazy-loaded too.
import TelegramLoginPage from "./pages/TelegramLoginPage";

// Wrap dynamic imports so that when a chunk goes missing after a deploy
// (old hash retired, browser/WebView still has the stale index.html), we
// reload the page once to fetch a fresh index.html with up-to-date hashes
// instead of spinning forever in <Suspense>.
const RELOAD_FLAG = "sayhi:chunkReloaded";
const safeImport = (loader) =>
  lazy(() =>
    loader().catch((err) => {
      const isChunkErr =
        /Importing a module script failed|Failed to fetch dynamically imported module|Loading chunk \d+ failed|Loading CSS chunk \d+ failed/i.test(
          String(err?.message || err)
        );
      if (isChunkErr && !sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, "1");
        window.location.reload();
      }
      throw err;
    })
  );

const LoginPage = safeImport(() => import("./pages/LoginPage"));
const SignUpPage = safeImport(() => import("./pages/SignUpPage"));
const SettingsPage = safeImport(() => import("./pages/SettingsPage"));
const ProfilePage = safeImport(() => import("./pages/ProfilePage"));
const MessagesPage = safeImport(() => import("./pages/MessagesPage"));
const ContactsPage = safeImport(() => import("./pages/ContactsPage"));
const ChatPage = safeImport(() => import("./pages/ChatPage"));
const TelegramProfilePage = safeImport(() =>
  import("./pages/TelegramProfilePage")
);
const InviteFriendsPage = safeImport(() => import("./pages/InviteFriendsPage"));

import { useAuthStore } from "./store/useAuthStore";
import { setReporterContext } from "./lib/errorReporter";
import { t } from "./lib/theme";

const RouteFallback = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: t.bgGrad,
    }}
  >
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 999,
        border: `3px solid ${t.primarySoft}`,
        borderTopColor: t.primary,
        animation: "spin 0.8s linear infinite",
      }}
    />
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  </div>
);

const App = () => {
  const { authUser, checkAuth, isCheckingAuth } = useAuthStore();
  const [telegramUser, setTelegramUser] = useState(null);

  useEffect(() => {
    if (PREVIEW) {
      setReporterContext({ preview: true });
      return;
    }
    try {
      const user = WebApp.initDataUnsafe?.user;
      // Raw signed string. Backend HMAC-verifies this with the bot token —
      // we do NOT trust telegram_id from the parsed `user` blob alone.
      const initData = WebApp.initData;
      if (user) {
        setTelegramUser({ ...user, telegram_id: user.id, initData });
        setReporterContext({
          tgId: user.id,
          tgUsername: user.username,
          platform: WebApp.platform,
          tgVersion: WebApp.version,
        });
      }
    } catch (e) {
      console.warn("Telegram initData unavailable", e);
    }
  }, []);

  useEffect(() => {
    if (!PREVIEW) checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (authUser?._id)
      setReporterContext({
        userId: authUser._id,
        fullname: authUser.fullname,
      });
  }, [authUser?._id]);

  // Presence heartbeat — bump presenceTick once a minute so components
  // subscribed to it re-render. Lets the simulated-online dot on fake
  // users tick along with UTC 10-min buckets instead of freezing at
  // page-load time. Cheap: only components that subscribe re-render,
  // and each does <0.1ms of work.
  useEffect(() => {
    const tick = useAuthStore.getState().tickPresence;
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, []);

  if (PREVIEW) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<MessagesPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/profile/:userid" element={<ProfilePage />} />
          <Route path="/conversation/:userId" element={<ChatPage />} />
          <Route
            path="/login"
            element={
              <TelegramLoginPage
                telegramUser={{
                  id: 1,
                  first_name: "John",
                  username: "john",
                  photo_url: "",
                  telegram_id: 1,
                }}
              />
            }
          />
          <Route path="/onboarding" element={<TelegramProfilePage />} />
          <Route path="/invite" element={<InviteFriendsPage />} />
        </Routes>
        <Toaster
        position="top-center"
        gutter={8}
        containerStyle={{ top: 12 }}
        toastOptions={TOAST_STYLE}
      />
      </Suspense>
    );
  }

  if (isCheckingAuth && !authUser) {
    return <RouteFallback />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route
          path="/"
          element={authUser ? <MessagesPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/signup"
          element={!authUser ? <SignUpPage /> : <MessagesPage />}
        />
        <Route
          path="/login"
          element={
            !authUser ? (
              telegramUser ? (
                <TelegramLoginPage telegramUser={telegramUser} />
              ) : (
                <LoginPage />
              )
            ) : authUser.isNewUser ? (
              <TelegramProfilePage />
            ) : (
              <MessagesPage />
            )
          }
        />
        <Route
          path="/profile/:userid"
          element={authUser ? <ProfilePage /> : <Navigate to="/login" />}
        />
        <Route
          path="/settings"
          element={authUser ? <SettingsPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/contacts"
          element={authUser ? <ContactsPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/messages"
          element={authUser ? <MessagesPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/conversation/:userId"
          element={authUser ? <ChatPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/invite"
          element={authUser ? <InviteFriendsPage /> : <Navigate to="/login" />}
        />
      </Routes>

      <Toaster
        position="top-center"
        gutter={8}
        containerStyle={{ top: 12 }}
        toastOptions={TOAST_STYLE}
      />
    </Suspense>
  );
};

export default App;
